import { Alert, Box, Button, Collapse, Stack, Typography } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { useEffect, useMemo, useState } from 'react'
import { ColumnDef, ColumnSizingState, getCoreRowModel, useReactTable } from '@tanstack/react-table'
import { com } from '@eidu/web'
import useEntityTypes, { EMPTY_ENTITY_TYPES } from '../../io/useEntityTypes'
import { useFetchEntityType } from '../../io/useEntityType'
import { usePermissionPoliciesByIds } from '../../io/usePermissionPolicies'
import useEntitiesOfType from '../../io/useEntitiesOfType'
import { findEntityTypeByFieldId } from '../../util/EntityTypeExtensions'
import EntityWithLabelAndRelated from '../../domain/entity/EntityWithLabelAndRelated'
import EqualityHashMap from '../../util/EqualityHashMap'
import computeActionsBreakdown, { ActionsBreakdown } from '../../domain/permission/computeActionsBreakdown'
import { StringListCell, TypesPermissionsCell } from './permissionListCells'
import InfiniteScrollTable from '../../components/data/InfiniteScrollTable'
import usePaginatedPermissionGrants, { FetchPermissionGrantsPage } from '../../io/usePaginatedPermissionGrants'
import { PermissionGrantDTO } from '../../domain/permission/PermissionGrantDTO'
import EntitySelectorChips from '../../components/input/EntitySelectorChips'
import AccessControlTabs from './AccessControlTabs'
import useHasGlobalPermissions from './useHasGlobalPermissions'
import EntityType = com.eidu.sharedlib.entity.type.EntityType
import EntityTypeId = com.eidu.sharedlib.entity.type.EntityTypeId
import EntityId = com.eidu.sharedlib.entity.EntityId
import EntitySelector = com.eidu.sharedlib.entity.selector.EntitySelector
import PermissionPolicyId = com.eidu.sharedlib.entity.permission.PermissionPolicyId
import GlobalAction = com.eidu.sharedlib.entity.permission.GlobalAction

const flattenUserSelectors = (selectors: EntitySelector[]): EntitySelector.OfType[] => {
  const andSelectors = selectors.filter((it) => it instanceof EntitySelector.And) as EntitySelector.And[]
  const orSelectors = selectors.filter((it) => it instanceof EntitySelector.Or) as EntitySelector.Or[]
  return selectors
    .filter((it) => it instanceof EntitySelector.OfType)
    .concat(
      ...andSelectors.map((it) => flattenUserSelectors([...it.selectors.asJsReadonlyArrayView()])),
      ...orSelectors.map((it) => flattenUserSelectors([...it.selectors.asJsReadonlyArrayView()]))
    )
}

type FieldBasedSelector =
  | EntitySelector.FieldValueEquals
  | EntitySelector.FieldValueEqualsUserEntityId
  | EntitySelector.FieldValueEqualsUserFieldValue

const flattenFieldBasedSelectors = (selectors: EntitySelector[]): FieldBasedSelector[] =>
  selectors.flatMap((it) => {
    if (
      it instanceof EntitySelector.FieldValueEquals ||
      it instanceof EntitySelector.FieldValueEqualsUserEntityId ||
      it instanceof EntitySelector.FieldValueEqualsUserFieldValue
    )
      return [it]
    if (it instanceof EntitySelector.And || it instanceof EntitySelector.Or)
      return flattenFieldBasedSelectors([...it.selectors.asJsReadonlyArrayView()])
    if (it instanceof EntitySelector.Not) return flattenFieldBasedSelectors([it.selector])
    return []
  })

const useUserSelectorsEntityTypes = (selectors: EntitySelector.OfType[]): ReadonlyMap<EntityTypeId, EntityType> => {
  const fetchEntityType = useFetchEntityType()
  const [entityTypes, setEntityTypes] = useState<EqualityHashMap<EntityTypeId, EntityType>>(new EqualityHashMap())

  useEffect(() => {
    const getEntityTypes = async () => {
      const types = await Promise.all(selectors.map((it) => fetchEntityType(it.typeId)))
      const nonNullTypes = types.filter((type): type is EntityType => type !== null)
      setEntityTypes(
        new EqualityHashMap(
          nonNullTypes.reduce((map, type) => map.set(type.id, type), new Map<EntityTypeId, EntityType>())
        )
      )
    }
    getEntityTypes().catch(console.error)
  }, [selectors, fetchEntityType])

  return entityTypes
}

const EMPTY_ENTITIES_BY_TYPE_ID: ReadonlyMap<
  EntityTypeId,
  ReadonlyMap<EntityId, EntityWithLabelAndRelated>
> = new EqualityHashMap()

const useReferencableEntitiesByTypeId = (
  entityTypes: ReadonlyMap<EntityTypeId, EntityType> | undefined,
  allEntityTypes: ReadonlyMap<EntityTypeId, EntityType | null> | undefined
): ReadonlyMap<EntityTypeId, ReadonlyMap<EntityId, EntityWithLabelAndRelated>> => {
  const referencableEntityTypeIds = useMemo(
    () =>
      Array.from(entityTypes?.values() ?? [])
        .flatMap((type) => type.fields.asJsReadonlyArrayView())
        .flatMap((field) => Array.from(field.validReferenceTypes?.asJsReadonlySetView() ?? [])),
    [entityTypes]
  )

  return (
    useEntitiesOfType({ typeIds: referencableEntityTypeIds, allTypes: allEntityTypes ?? EMPTY_ENTITY_TYPES }) ??
    EMPTY_ENTITIES_BY_TYPE_ID
  )
}

type GrantRow = {
  id: string
  rowNumber: number
  grant: PermissionGrantDTO
  actions: ActionsBreakdown
  policies: string[]
}

type PermissionGrantsListPageContentProps = {
  pageSize: number
  fetchPage?: FetchPermissionGrantsPage
  onNewClicked: () => void
  onViewerClicked: () => void
  onPermissionClicked: (id: string) => void
}

const PermissionGrantsListPageContent = ({
  pageSize,
  fetchPage,
  onNewClicked,
  onViewerClicked,
  onPermissionClicked,
}: PermissionGrantsListPageContentProps) => {
  const { grants, totalCount, hasMore, loading, loadMore } = usePaginatedPermissionGrants({ pageSize, fetchPage })

  const { entityTypes: allEntityTypes } = useEntityTypes()
  const referencedPolicyIds = useMemo(() => {
    const map = new EqualityHashMap<PermissionPolicyId, PermissionPolicyId>()
    for (const grant of grants) {
      for (const id of grant.permissionGrant.policyIds.asJsReadonlySetView()) map.set(id, id)
    }
    return [...map.values()]
  }, [grants])
  const referencedPoliciesQuery = usePermissionPoliciesByIds(referencedPolicyIds)
  const policyNamesById = useMemo(() => {
    const map = new EqualityHashMap<PermissionPolicyId, string>()
    for (const policy of referencedPoliciesQuery.policies) map.set(policy.policyId, policy.policy.name)
    return map
  }, [referencedPoliciesQuery.policies])
  const userSelectors = useMemo(
    () => flattenUserSelectors(grants.map((it) => it.permissionGrant.userSelector)),
    [grants]
  )
  const userSelectorEntityTypes = useUserSelectorsEntityTypes(userSelectors)
  const entityTypes = useMemo(() => {
    const map = new EqualityHashMap<EntityTypeId, EntityType>(userSelectorEntityTypes)
    if (allEntityTypes)
      [...allEntityTypes.entries()].forEach(([id, type]) => {
        if (type && !map.has(id)) map.set(id, type)
      })
    return map
  }, [userSelectorEntityTypes, allEntityTypes])
  const fieldSelectorEntityTypes = useMemo(() => {
    const map = new EqualityHashMap<EntityTypeId, EntityType>()
    const types = [...entityTypes.values()]
    if (types.length === 0) return map
    const fieldSelectors = flattenFieldBasedSelectors(grants.map((it) => it.permissionGrant.userSelector))
    fieldSelectors.forEach((selector) => {
      const owning = findEntityTypeByFieldId(types, selector.fieldId)
      if (owning) map.set(owning.id, owning)
      if (selector instanceof EntitySelector.FieldValueEqualsUserFieldValue) {
        const userOwning = findEntityTypeByFieldId(types, selector.userFieldId)
        if (userOwning) map.set(userOwning.id, userOwning)
      }
    })
    return map
  }, [grants, entityTypes])
  const entityTypesForReferenceLoading = useMemo(() => {
    const map = new EqualityHashMap<EntityTypeId, EntityType>(userSelectorEntityTypes)
    fieldSelectorEntityTypes.forEach((type, id) => {
      if (!map.has(id)) map.set(id, type)
    })
    return map
  }, [userSelectorEntityTypes, fieldSelectorEntityTypes])
  const entitiesByTypeId = useReferencableEntitiesByTypeId(entityTypesForReferenceLoading, allEntityTypes)

  const rows: GrantRow[] = useMemo(
    () =>
      grants.map((grant, index) => ({
        id: grant.grantId.toString(),
        rowNumber: index + 1,
        grant,
        actions: computeActionsBreakdown([...grant.permissionGrant.permissions.asJsReadonlySetView()], entityTypes),
        policies: [...grant.permissionGrant.policyIds.asJsReadonlySetView()]
          .map((id) => policyNamesById.get(id) ?? `Unknown (${id.toString().slice(0, 8)})`)
          .sort((a, b) => a.localeCompare(b)),
      })),
    [grants, entityTypes, policyNamesById]
  )

  const columns = useMemo<ColumnDef<GrantRow>[]>(
    () => [
      {
        id: 'row',
        header: () => <span>Row</span>,
        accessorKey: 'rowNumber',
        size: 70,
        minSize: 50,
        enableResizing: false,
      },
      {
        id: 'users',
        header: () => <span>Users</span>,
        cell: ({ row }) => (
          <EntitySelectorChips
            selector={row.original.grant.permissionGrant.userSelector}
            entityTypes={entityTypes}
            entitiesByTypeId={entitiesByTypeId}
            direction="row"
          />
        ),
        size: 400,
        minSize: 200,
      },
      {
        id: 'globalPermissions',
        header: () => <span>Global permissions</span>,
        cell: ({ row }) => <StringListCell value={row.original.actions.global} />,
        size: 300,
        minSize: 150,
      },
      {
        id: 'typesPermissions',
        header: () => <span>Types permissions</span>,
        cell: ({ row }) => <TypesPermissionsCell value={row.original.actions.perType} />,
        size: 400,
        minSize: 200,
      },
      {
        id: 'policies',
        header: () => <span>Policies</span>,
        cell: ({ row }) => <StringListCell value={row.original.policies} />,
        size: 300,
        minSize: 150,
      },
    ],
    [entityTypes, entitiesByTypeId]
  )

  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>({})

  const table = useReactTable({
    data: rows,
    columns,
    state: { columnSizing },
    onColumnSizingChange: setColumnSizing,
    columnResizeMode: 'onChange',
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row.id,
  })

  return (
    <Stack
      sx={{
        flex: 1,
        minHeight: 0,
        p: 3,
        overflow: 'hidden',
      }}
      spacing={2}
    >
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          overflow: 'hidden',
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            px: 2,
            py: 1,
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            borderBottom: 1,
            borderColor: 'divider',
            flexShrink: 0,
          }}
        >
          <Stack>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Access Control
            </Typography>
            {totalCount !== undefined && (
              <Typography variant="caption" sx={{ opacity: 0.85 }}>
                {totalCount} total
              </Typography>
            )}
          </Stack>
          <Stack direction="row" spacing={1}>
            <Button color="inherit" size="small" onClick={onViewerClicked}>
              Visualizer
            </Button>
            <Button color="inherit" variant="outlined" size="small" onClick={onNewClicked}>
              Grant permission
            </Button>
          </Stack>
        </Stack>
        <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <InfiniteScrollTable
            table={table}
            loading={loading}
            hasMore={hasMore}
            loadMore={loadMore}
            emptyMessage="No permission grants"
            containerTestId="permission-grants-table"
            onRowClick={(row) => onPermissionClicked(row.id)}
            estimateRowSize={56}
          />
        </Box>
      </Box>
    </Stack>
  )
}

type PermissionGrantsListPageProps = {
  pageSize?: number
  fetchPage?: FetchPermissionGrantsPage
}

const PermissionGrantsListPage = ({ pageSize = 50, fetchPage }: PermissionGrantsListPageProps) => {
  const navigate = useNavigate()

  const { error } = useEntityTypes()

  const { hasPermission, loading: permissionsLoading } = useHasGlobalPermissions(GlobalAction.ManagePermissionGrants)

  const hasManagePermissionGrantsPermission = hasPermission(GlobalAction.ManagePermissionGrants)

  if (permissionsLoading || hasManagePermissionGrantsPermission === false) return null

  return (
    <Stack sx={{ flex: 1, minHeight: 0 }}>
      <AccessControlTabs />
      <Collapse in={!!error}>
        <Alert
          variant="filled"
          severity="error"
          sx={{ margin: 2 }}
          action={
            <Button color="inherit" size="small" onClick={() => window.location.reload()}>
              Reload
            </Button>
          }
        >
          <Box>{String(error)}</Box>
          <Box>Please reload the page and try again.</Box>
        </Alert>
      </Collapse>
      {!error && (
        <PermissionGrantsListPageContent
          pageSize={pageSize}
          fetchPage={fetchPage}
          onNewClicked={() => navigate('new')}
          onViewerClicked={() => navigate('/access-control/visualizer')}
          onPermissionClicked={navigate}
        />
      )}
    </Stack>
  )
}

const PermissionGrantsListPageRoute = () => <PermissionGrantsListPage />

export { PermissionGrantsListPage }
export default PermissionGrantsListPageRoute
